import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  useToast,
  Badge,
  Spinner,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useDisclosure,
  Divider,
} from '@chakra-ui/react';
import { FiSearch, FiRefreshCw, FiEye, FiCalendar, FiDollarSign, FiDownload, FiShoppingBag, FiUsers, FiPackage, FiMapPin } from 'react-icons/fi';
import DataTable from 'react-data-table-component';
import { productServicesApi } from '../config/api';

const ProductWrappingTab = () => {
  const toast = useToast();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [searchText, setSearchText] = useState('');
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  
  const [syncDates, setSyncDates] = useState({
    dateFrom: '',
    dateTo: ''
  });
  
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    transactionNumber: '',
    branchCode: '',
    cashierName: '',
    productName: '',
    limit: 10,
    offset: 0
  });

  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const params = { ...filters };
      
      // Remove empty values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await productServicesApi.get('/list', { params });

      if (response.data && response.data.status === 200) {
        setTransactions(response.data.data || []);
        setTotalRows(response.data.total || 0);
      } else {
        toast({
          title: 'Error',
          description: response.data?.message || 'Failed to fetch transactions',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to fetch transactions',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  // Fetch summary - use same filters as transactions
  const fetchSummary = useCallback(async () => {
    try {
      const params = {};
      // Apply all filters that affect summary
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.branchCode) params.branchCode = filters.branchCode;
      if (filters.transactionNumber) params.transactionNumber = filters.transactionNumber;
      if (filters.cashierName) params.cashierName = filters.cashierName;
      if (filters.productName) params.productName = filters.productName;

      const response = await productServicesApi.get('/summary/stats', { params });

      if (response.data && response.data.status === 200) {
        setSummary(response.data.data || {});
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [filters]);

  useEffect(() => {
    fetchTransactions();
    fetchSummary();
  }, [fetchTransactions, fetchSummary]);

  // Fetch transaction detail
  const fetchTransactionDetail = async (id) => {
    try {
      const response = await productServicesApi.get(`/${id}`);
      if (response.data && response.data.status === 200) {
        setSelectedTransaction(response.data.data);
        onDetailOpen();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to fetch transaction detail',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle filter change
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
      offset: 0 // Reset offset when filter changes
    }));
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      transactionNumber: '',
      branchCode: '',
      cashierName: '',
      productName: '',
      limit: 10,
      offset: 0
    });
  };

  // Handle sync
  const handleSync = async () => {
    if (!syncDates.dateFrom || !syncDates.dateTo) {
      toast({
        title: 'Validation Error',
        description: 'Please select both start date and end date',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setSyncing(true);
      const response = await productServicesApi.post('/sync', {
        dateFrom: syncDates.dateFrom,
        dateTo: syncDates.dateTo
      }, {
        timeout: 120000 // 2 minutes for sync operation
      });

      if (response.data && response.data.status === 200) {
        const result = response.data.data;
        toast({
          title: 'Sync Successful',
          description: `Synced ${result.successCount} transactions. ${result.errorCount > 0 ? `${result.errorCount} errors occurred.` : ''}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Refresh transactions and summary after sync
        fetchTransactions();
        fetchSummary();
      } else {
        toast({
          title: 'Error',
          description: response.data?.message || 'Failed to sync data',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error syncing data:', error);
      let errorMessage = 'Failed to sync data';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Request timeout. The sync operation is taking longer than expected. Please try again or reduce the date range.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSyncing(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return 'Rp 0';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(numValue);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter transactions based on search text (client-side)
  const filteredTransactions = React.useMemo(() => {
    if (!searchText) return transactions;
    const searchLower = searchText.toLowerCase();
    return transactions.filter(row => 
      (row.PRODUCTSERVICES_TRANSACTIONNUMBER && row.PRODUCTSERVICES_TRANSACTIONNUMBER.toLowerCase().includes(searchLower)) ||
      (row.PRODUCTSERVICES_BRANCHCODE && row.PRODUCTSERVICES_BRANCHCODE.toLowerCase().includes(searchLower)) ||
      (row.PRODUCTSERVICES_CASHIERNAME && row.PRODUCTSERVICES_CASHIERNAME.toLowerCase().includes(searchLower)) ||
      (row.PRODUCTSERVICES_PRODUCTNAME && row.PRODUCTSERVICES_PRODUCTNAME.toLowerCase().includes(searchLower))
    );
  }, [transactions, searchText]);

  // DataTable columns
  const columns = [
    {
      name: 'Transaction Date',
      selector: row => formatDate(row.PRODUCTSERVICES_TRANSACTIONDATE),
      sortable: true,
      minWidth: '160px',
      wrap: true
    },
    {
      name: 'Transaction Number',
      selector: row => row.PRODUCTSERVICES_TRANSACTIONNUMBER || '-',
      sortable: true,
      minWidth: '180px',
      wrap: true
    },
    {
      name: 'Branch Code',
      selector: row => row.PRODUCTSERVICES_BRANCHCODE || '-',
      sortable: true,
      minWidth: '120px',
      wrap: true
    },
    {
      name: 'Cashier',
      selector: row => row.PRODUCTSERVICES_CASHIERNAME || '-',
      sortable: true,
      minWidth: '150px',
      wrap: true
    },
    {
      name: 'Product Name',
      selector: row => row.PRODUCTSERVICES_PRODUCTNAME || '-',
      sortable: true,
      minWidth: '200px',
      wrap: true
    },
    {
      name: 'Quantity',
      selector: row => row.PRODUCTSERVICES_QUANTITY || '0',
      sortable: true,
      minWidth: '100px',
      right: true
    },
    {
      name: 'Subtotal',
      selector: row => row.PRODUCTSERVICES_SUBTOTAL || 0,
      sortable: true,
      cell: row => (
        <Text fontWeight="semibold" fontSize="sm">{formatCurrency(row.PRODUCTSERVICES_SUBTOTAL)}</Text>
      ),
      minWidth: '130px',
      right: true
    },
    {
      name: 'Actions',
      cell: row => (
        <Button
          size="xs"
          leftIcon={<FiEye />}
          onClick={() => fetchTransactionDetail(row.PRODUCTSERVICES_ID)}
          colorScheme="blue"
        >
          View
        </Button>
      ),
      minWidth: '80px',
      right: true
    }
  ];

  return (
    <VStack align="stretch" spacing={4}>
      <Box bg="blue.50" p={4} borderRadius="md" border="1px solid" borderColor="blue.100">
        <Text fontSize="sm" color="blue.800">
          Area ini khusus untuk sinkronisasi dan reporting transaksi IAS. Untuk import source product ke PRIME, gunakan menu
          <strong> Import Source IAS API</strong> pada Product Workspace.
        </Text>
      </Box>
      {/* Sync Section */}
      <Box bg="white" p={4} borderRadius="md" boxShadow="sm" borderLeft="4px" borderColor="blue.500">
        <VStack align="stretch" spacing={4}>
          <HStack>
            <Text fontWeight="bold" fontSize="lg">Sync Data from IAS API</Text>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FiCalendar color="gray.300" />
              </InputLeftElement>
              <Input
                type="date"
                placeholder="Start Date"
                value={syncDates.dateFrom}
                onChange={(e) => setSyncDates(prev => ({ ...prev, dateFrom: e.target.value }))}
              />
            </InputGroup>
            
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FiCalendar color="gray.300" />
              </InputLeftElement>
              <Input
                type="date"
                placeholder="End Date"
                value={syncDates.dateTo}
                onChange={(e) => setSyncDates(prev => ({ ...prev, dateTo: e.target.value }))}
              />
            </InputGroup>
            
            <Button
              leftIcon={<FiDownload />}
              colorScheme="blue"
              onClick={handleSync}
              isLoading={syncing}
              loadingText="Syncing..."
              isDisabled={!syncDates.dateFrom || !syncDates.dateTo}
            >
              Sync Data
            </Button>
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Filters */}
      <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <FiCalendar color="gray.300" />
            </InputLeftElement>
            <Input
              type="date"
              placeholder="Date From"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </InputGroup>
          
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <FiCalendar color="gray.300" />
            </InputLeftElement>
            <Input
              type="date"
              placeholder="Date To"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </InputGroup>
          
          <Input
            placeholder="Transaction Number"
            value={filters.transactionNumber}
            onChange={(e) => handleFilterChange('transactionNumber', e.target.value)}
          />
          
          <Input
            placeholder="Branch Code"
            value={filters.branchCode}
            onChange={(e) => handleFilterChange('branchCode', e.target.value)}
          />
          
          <Input
            placeholder="Cashier Name"
            value={filters.cashierName}
            onChange={(e) => handleFilterChange('cashierName', e.target.value)}
          />
          
          <Input
            placeholder="Product Name"
            value={filters.productName}
            onChange={(e) => handleFilterChange('productName', e.target.value)}
          />
        </SimpleGrid>
        
        <HStack mt={4} spacing={2}>
          <Button
            leftIcon={<FiSearch />}
            colorScheme="blue"
            onClick={() => {
              fetchTransactions();
              fetchSummary();
            }}
            isLoading={loading}
          >
            Search
          </Button>
          <Button
            leftIcon={<FiRefreshCw />}
            onClick={handleResetFilters}
          >
            Reset
          </Button>
        </HStack>
      </Box>

      {/* Summary Cards */}
      {summary && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 6 }} spacing={4}>
          <Stat 
            bg="white" 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderLeft="4px"
            borderColor="blue.500"
          >
            <HStack spacing={3} mb={2}>
              <Box
                p={2}
                borderRadius="md"
                bg="blue.50"
                color="blue.600"
              >
                <FiShoppingBag size={20} />
              </Box>
              <StatLabel fontSize="sm" color="gray.600">Total Orders</StatLabel>
            </HStack>
            <StatNumber fontSize="2xl" color="blue.600">{summary.total_orders || 0}</StatNumber>
            <StatHelpText fontSize="xs">
              {summary.first_transaction_date && summary.last_transaction_date && (
                `${formatDate(summary.first_transaction_date)} - ${formatDate(summary.last_transaction_date)}`
              )}
            </StatHelpText>
          </Stat>
          
          <Stat 
            bg="white" 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderLeft="4px"
            borderColor="green.500"
          >
            <HStack spacing={3} mb={2}>
              <Box
                p={2}
                borderRadius="md"
                bg="green.50"
                color="green.600"
              >
                <FiUsers size={20} />
              </Box>
              <StatLabel fontSize="sm" color="gray.600">Total Cashiers</StatLabel>
            </HStack>
            <StatNumber fontSize="2xl" color="green.600">{summary.total_cashiers || 0}</StatNumber>
            <StatHelpText fontSize="xs">Unique cashiers</StatHelpText>
          </Stat>
          
          <Stat 
            bg="white" 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderLeft="4px"
            borderColor="purple.500"
          >
            <HStack spacing={3} mb={2}>
              <Box
                p={2}
                borderRadius="md"
                bg="purple.50"
                color="purple.600"
              >
                <FiPackage size={20} />
              </Box>
              <StatLabel fontSize="sm" color="gray.600">Total Quantity</StatLabel>
            </HStack>
            <StatNumber fontSize="2xl" color="purple.600">{summary.total_quantity || 0}</StatNumber>
            <StatHelpText fontSize="xs">Items sold</StatHelpText>
          </Stat>
          
          <Stat 
            bg="white" 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderLeft="4px"
            borderColor="orange.500"
          >
            <HStack spacing={3} mb={2}>
              <Box
                p={2}
                borderRadius="md"
                bg="orange.50"
                color="orange.600"
              >
                <FiDollarSign size={20} />
              </Box>
              <StatLabel fontSize="sm" color="gray.600">Total Amount</StatLabel>
            </HStack>
            <StatNumber fontSize="xl" color="orange.600">{formatCurrency(summary.total_amount || 0)}</StatNumber>
            <StatHelpText fontSize="xs">Total sales</StatHelpText>
          </Stat>
          
          <Stat 
            bg="white" 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderLeft="4px"
            borderColor="red.500"
          >
            <HStack spacing={3} mb={2}>
              <Box
                p={2}
                borderRadius="md"
                bg="red.50"
                color="red.600"
              >
                <FiDollarSign size={20} />
              </Box>
              <StatLabel fontSize="sm" color="gray.600">Total Discount</StatLabel>
            </HStack>
            <StatNumber fontSize="xl" color="red.600">{formatCurrency(summary.total_discount || 0)}</StatNumber>
            <StatHelpText fontSize="xs">Total discounts</StatHelpText>
          </Stat>
          
          <Stat 
            bg="white" 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderLeft="4px"
            borderColor="teal.500"
          >
            <HStack spacing={3} mb={2}>
              <Box
                p={2}
                borderRadius="md"
                bg="teal.50"
                color="teal.600"
              >
                <FiMapPin size={20} />
              </Box>
              <StatLabel fontSize="sm" color="gray.600">Total Branches</StatLabel>
            </HStack>
            <StatNumber fontSize="xl" color="teal.600">{summary.total_branches || 0}</StatNumber>
            <StatHelpText fontSize="xs">Branches</StatHelpText>
          </Stat>
        </SimpleGrid>
      )}

      {/* Transactions Table */}
      <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
        <style jsx global>{`
          .rdt_Table {
            width: 100%;
            overflow-x: auto;
          }
          .rdt_TableHeadRow {
            background-color: #f7fafc;
            font-weight: 600;
            font-size: 12px;
          }
          .rdt_TableRow {
            font-size: 13px;
          }
          .rdt_TableCell {
            padding: 12px 8px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .rdt_TableCol {
            padding: 12px 8px;
            white-space: nowrap;
          }
          .rdt_TableCol_Sortable {
            cursor: pointer;
          }
          .rdt_TableBody {
            overflow-x: auto;
          }
          @media (max-width: 768px) {
            .rdt_Table {
              font-size: 11px;
            }
            .rdt_TableCell {
              padding: 8px 4px;
            }
            .rdt_TableCol {
              padding: 8px 4px;
            }
          }
        `}</style>
        {loading ? (
          <Box textAlign="center" py={8}>
            <Spinner size="xl" />
          </Box>
        ) : (
          <Box width="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={searchText ? filteredTransactions : transactions}
              pagination
              paginationServer={!searchText}
              paginationTotalRows={searchText ? filteredTransactions.length : totalRows}
              paginationPerPage={filters.limit}
              paginationDefaultPage={searchText ? 1 : Math.floor(filters.offset / filters.limit) + 1}
              paginationRowsPerPageOptions={[10, 25, 50, 100, 200]}
              onChangePage={(page) => {
                if (!searchText) {
                  const newOffset = (page - 1) * filters.limit;
                  handleFilterChange('offset', newOffset);
                }
              }}
              onChangeRowsPerPage={(newLimit) => {
                handleFilterChange('limit', newLimit);
                handleFilterChange('offset', 0);
              }}
              subHeader
              subHeaderComponent={
                <Box mb={4}>
                  <InputGroup maxW="400px">
                    <InputLeftElement pointerEvents="none">
                      <FiSearch color="gray.300" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search by Transaction Number, Branch, Cashier, Product..."
                      value={searchText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchText(value);
                      }}
                    />
                  </InputGroup>
                </Box>
              }
              onRowClicked={(row) => fetchTransactionDetail(row.PRODUCTSERVICES_ID)}
              highlightOnHover
              pointerOnHover
              noDataComponent="No transactions found"
              responsive
              dense
              fixedHeader
              fixedHeaderScrollHeight="600px"
            />
          </Box>
        )}
      </Box>

      {/* Transaction Detail Modal */}
      <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Transaction Detail</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedTransaction && (
              <VStack align="stretch" spacing={4}>
                {/* Transaction Info */}
                <Box>
                  <Text fontWeight="bold" mb={2}>Transaction Information</Text>
                  <TableContainer>
                    <Table size="sm" variant="simple">
                      <Tbody>
                        <Tr>
                          <Th>Transaction Number</Th>
                          <Td>{selectedTransaction.PRODUCTSERVICES_TRANSACTIONNUMBER || '-'}</Td>
                        </Tr>
                        <Tr>
                          <Th>Transaction Date</Th>
                          <Td>{formatDate(selectedTransaction.PRODUCTSERVICES_TRANSACTIONDATE)}</Td>
                        </Tr>
                        <Tr>
                          <Th>Branch Code</Th>
                          <Td>{selectedTransaction.PRODUCTSERVICES_BRANCHCODE || '-'}</Td>
                        </Tr>
                        <Tr>
                          <Th>Cashier Name</Th>
                          <Td>{selectedTransaction.PRODUCTSERVICES_CASHIERNAME || '-'}</Td>
                        </Tr>
                        <Tr>
                          <Th>Product Name</Th>
                          <Td>{selectedTransaction.PRODUCTSERVICES_PRODUCTNAME || '-'}</Td>
                        </Tr>
                        <Tr>
                          <Th>Quantity</Th>
                          <Td>{selectedTransaction.PRODUCTSERVICES_QUANTITY || '0'}</Td>
                        </Tr>
                        <Tr>
                          <Th>Price</Th>
                          <Td>{formatCurrency(selectedTransaction.PRODUCTSERVICES_PRICE)}</Td>
                        </Tr>
                        <Tr>
                          <Th>Discount</Th>
                          <Td>{formatCurrency(selectedTransaction.PRODUCTSERVICES_DISCOUNT)}</Td>
                        </Tr>
                        <Tr>
                          <Th>Subtotal</Th>
                          <Td fontWeight="bold">{formatCurrency(selectedTransaction.PRODUCTSERVICES_SUBTOTAL)}</Td>
                        </Tr>
                        {selectedTransaction.PRODUCTSERVICES_PROMOCODE && (
                          <Tr>
                            <Th>Promo Code</Th>
                            <Td>{selectedTransaction.PRODUCTSERVICES_PROMOCODE}</Td>
                          </Tr>
                        )}
                        {selectedTransaction.PRODUCTSERVICES_PROMOREMARK && (
                          <Tr>
                            <Th>Promo Remark</Th>
                            <Td>{selectedTransaction.PRODUCTSERVICES_PROMOREMARK}</Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onDetailClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default ProductWrappingTab;
